// Hebrew string catalogue — keyed by string id
// (docs/design/14-ui-component-spec.md §4). Error codes come from
// docs/design/10-signaling-protocol.md §5.

export const he = {
  // Generic
  app: {
    name: "שימוע וידאו",
    policeBrand: "שימוע וידאו — משטרת ישראל",
    startupError: "אירעה תקלה בטעינת המערכת — נא לרענן או לפנות לתמיכה."
  },
  nav: {
    home: "בית",
    schedule: "פגישות",
    admin: "ניהול",
    switchLanguage: "החלפת שפה",
    placeholder: "מסך זה ייבנה בשלבי הפיתוח הבאים. ראו"
  },
  // Status / banner
  banner: {
    reconnecting: "מתחבר מחדש…",
    recordingOn: "מוקלט"
  },
  // Embedded launch context (modules/04 §3)
  launch: {
    caseContext: "פרטי תיק",
    driver: "ת״ז נהג",
    caseRef: "אסמכתא",
    system: "מערכת מקור"
  },
  // Call flow (docs/design/05-uiux-spec.md §3)
  call: {
    precallTitle: "בחירת קצין לשימוע",
    search: "חיפוש קצין…",
    available: "זמין",
    unavailable: "לא זמין",
    callAction: "התקשר",
    noOfficers: "אין קצינים זמינים כעת.",
    callingTitle: "מתקשר…",
    cancel: "ביטול",
    incomingTitle: "שיחה נכנסת",
    accept: "ענה",
    decline: "דחה",
    endCall: "סיים שיחה",
    endedTitle: "השיחה הסתיימה",
    backToList: "חזרה לרשימה",
    mute: "השתק",
    unmute: "בטל השתקה",
    camOff: "כבה מצלמה",
    camOn: "הפעל מצלמה",
    selfPanel: "השוטר והאזרח",
    officerPanel: "הקצין",
    availabilityToggle: "סטטוס זמינות",
    shareScreen: "שיתוף מסך",
    stopShare: "הפסק שיתוף",
    screenSharing: "משתף מסך",
    controlsLabel: "בקרת שיחה",
    fullscreen: "מסך מלא",
    exitFullscreen: "יציאה ממסך מלא",
    officerStation: "עמדת קצין",
    waitingTitle: "ממתין לשיחה נכנסת",
    waitingStation: "עמדה זו פנויה וזמינה לקבלת שיחות נכנסות.",
    localPreview: "תצוגה מקומית",
    tokenExpired: "פג תוקף החיבור. יש לפתוח את קישור ההדגמה מחדש.",
    connectFailed: "לא ניתן להתחבר לשירות השיחות. יש לרענן את הדף או לפתוח את קישור ההדגמה מחדש.",
    missingCallId: "לא נמצא מזהה שיחה לפתיחת ההתקשרות.",
    hubNotReady: "שירות השיחות עדיין לא מחובר. יש לרענן את הדף או לפתוח את קישור ההדגמה מחדש.",
    startFailed: "לא ניתן להתחיל את השיחה.",
    callerFallback: "שוטר"
  },
  // Device readiness ("get ready before the call") — design/05
  readiness: {
    title: "בדיקת מצלמה ומיקרופון",
    subtitle: "יש לוודא שהמצלמה והמיקרופון פועלים לפני תחילת השיחה.",
    camera: "מצלמה",
    microphone: "מיקרופון",
    cameraOn: "מצלמה פועלת",
    cameraOff: "מצלמה כבויה",
    micOn: "מיקרופון פועל",
    micOff: "מיקרופון מושתק",
    cameraOffPreview: "המצלמה כבויה",
    continueTraffic: "מוכן/ה — התחל לקבל שיחות",
    continueField: "המשך לבחירת קצין",
    retry: "נסה שוב",
    requesting: "מבקש גישה למצלמה ולמיקרופון…",
    noDevice: "לא נמצאו מצלמה או מיקרופון זמינים — יש לחבר התקן ולנסות שוב."
  },
  // Availability (preference 2)
  availability: {
    available: "זמין",
    unavailable: "לא זמין",
    title: "סטטוס זמינות"
  },
  // Scheduling (preference 1)
  schedule: {
    title: "פגישות מתוזמנות",
    none: "אין פגישות מתוזמנות.",
    join: "הצטרף",
    cancel: "בטל",
    start: "מועד",
    duration: "משך (דק׳)",
    caseRef: "אסמכתא"
  },
  // Admin console (design/05 §6)
  admin: {
    title: "מסוף ניהול",
    officers: "קצינים",
    recordings: "הקלטות",
    dashboard: "לוח בקרה",
    config: "תצורה",
    // Shown when the admin APIs return 401/403 — no admin-authorized session is present.
    notAuthorized: "נדרשת הרשאת ניהול לצפייה במסוף זה. יש להיכנס עם חשבון מנהל או צופה IT.",
    sessionExpired: "ההתחברות פגה. יש לפתוח מחדש את קישור הניהול עם חשבון מנהל.",
    // Shown briefly while redirecting to the demo admin entry to mint a fresh admin token.
    redirecting: "מפנה לכניסת מנהל…"
  },
  // Admin operational dashboard (design/05 §6)
  dash: {
    subtitle: "מצב תפעולי של מערכת השימוע",
    updated: "עודכן",
    refresh: "רענון",
    sectionStatus: "מצב תפעולי",
    sectionHealth: "תקינות שירותים",
    sectionRecordings: "סטטוס הקלטות",
    sectionOfficers: "זמינות קצינים",
    sectionActivity: "פעילות אחרונה",
    sectionMetrics: "מדדי תשתית וניטור",
    officersAvailable: "קצינים זמינים",
    officersUnavailable: "לא זמינים",
    recInProgress: "הקלטות בעיבוד",
    recCompleted: "הקלטות שהושלמו",
    recFailed: "הקלטות שנכשלו",
    recQueued: "בתור",
    recSigned: "חתומות דיגיטלית",
    storageUsed: "נפח אחסון",
    totalMinutes: "דקות הקלטה",
    latestRecording: "הקלטה אחרונה",
    of: "מתוך",
    serviceUp: "פעיל",
    serviceDown: "לא זמין",
    metricsNotReported: "מדדי התשתית (ניטור) טרם דווחו. הנתונים התפעוליים למעלה משקפים את מצב המערכת בפועל.",
    noActivity: "אין פעילות הקלטות אחרונה.",
    secondsUnit: "שנ׳",
    // Service display names (raw health keys → Hebrew); unknown keys fall back to the raw name.
    services: {
      "app-service": "שירות יישום",
      "media-service": "שירות מדיה",
      "compositing-worker": "עיבוד הקלטות"
    } as Record<string, string>,
    // Hebrew labels for the raw KPI names (shown only when a KPI is non-zero).
    kpi: {
      vc_active_calls: "שיחות פעילות",
      vc_configured_capacity: "קיבולת מוגדרת",
      vc_available_slots: "עמדות פנויות",
      vc_capacity_utilization_ratio: "ניצולת קיבולת",
      vc_call_setup_seconds_p95: "זמן חיבור (p95)",
      vc_call_failure_ratio: "שיעור כשל שיחות",
      vc_reconnect_ratio: "שיעור חיבור מחדש",
      vc_compositing_queue_depth: "תור עיבוד הקלטות",
      vc_compositing_latency_seconds_p95: "זמן עיבוד (p95)",
      vc_compositing_failed_total: "כשלי עיבוד",
      vc_recording_capture_faults_total: "תקלות לכידה",
      vc_scratch_store_free_ratio: "אחסון זמני פנוי",
      vc_recording_store_free_ratio: "אחסון הקלטות פנוי",
      vc_cpu_headroom_ratio: "עתודת מעבד"
    } as Record<string, string>
  },
  // Admin recordings surface
  rec: {
    search: "חיפוש (מערכת / אסמכתא / ת״ז / שם קובץ)",
    statusAll: "כל הסטטוסים",
    statusCompleted: "הושלם",
    statusQueued: "בתור",
    statusCompositing: "בעיבוד",
    statusCapturing: "בהקלטה",
    statusFailed: "נכשל",
    refresh: "רענון",
    none: "לא נמצאו הקלטות.",
    colStatus: "סטטוס",
    colCall: "שיחה",
    colCase: "מערכת / תיק / נהג",
    colDuration: "משך",
    colSize: "גודל",
    colCreated: "נוצר",
    colCompleted: "הושלם",
    play: "נגן",
    download: "הורדה",
    manifest: "מניפסט",
    signature: "חתימה",
    details: "פרטי הקלטה",
    notPlayable: "הקלטה זו אינה זמינה לצפייה (טרם הושלמה או הקובץ חסר).",
    seconds: "שנ׳",
    close: "סגור",
    loading: "טוען פרטי הקלטה…",
    selectHint: "בחר הקלטה מהרשימה כדי לצפות בה ולהורידה.",
    authTitle: "כניסה לצפייה בהקלטה",
    authHint: "נדרש שם משתמש וסיסמה נוספים רק להפעלת נגן ההקלטה.",
    username: "שם משתמש",
    password: "סיסמה",
    authSubmit: "כניסה",
    authChecking: "בודק…",
    authFailed: "שם המשתמש או הסיסמה אינם תקינים.",
    authExpired: "הרשאת הצפייה בהקלטה פגה. יש להיכנס שוב."
  },
  // Errors (mapped from VC-* codes — see design/10 §5)
  errors: {
    "VC-AUTH-001": "ההתחברות פגה — יש להיכנס מחדש.",
    "VC-AUTH-002": "אין לך הרשאה לפעולה זו.",
    "VC-LAUNCH-001": "פרטי הפתיחה חסרים או שגויים — נא לפנות למערכת המארחת.",
    "VC-CALL-001": "הקצין אינו זמין כעת.",
    "VC-CALL-002": "אין מענה — ניתן לנסות שוב או לבחור קצין אחר.",
    "VC-CALL-003": "הקצין דחה את השיחה.",
    "VC-CALL-004": "המערכת בעומס מלא — נא לנסות שוב בעוד מספר דקות.",
    "VC-MEDIA-001": "נדרשת גישה למצלמה ולמיקרופון.",
    "VC-MEDIA-002": "תקלת מדיה — מתבצע חיבור מחדש…",
    "VC-MEDIA-003": "החיבור נכשל — השיחה הסתיימה. ההקלטה נשמרה.",
    "VC-MEDIA-004": "נדרש חיבור מאובטח (HTTPS) כדי לגשת למצלמה ולמיקרופון — יש לפתוח את ההדגמה בכתובת מאובטחת.",
    "VC-REC-001": "תקלה בהקלטה — נא לפנות לתמיכה.",
    "VC-CFG-001": "ערך תצורה אינו תקין.",
    "VC-SCHED-001": "פרטי הפגישה אינם תקינים — נא לבדוק את המשך והמשתתפים.",
    "VC-SCHED-002": "לא ניתן לבטל פגישה שהסתיימה.",
    "VC-SCHED-404": "הפגישה לא נמצאה.",
    "VC-RESYNC": "החיבור התחדש — מסנכרן מחדש את מצב השיחה…",
    "VC-SYS-001": "אירעה תקלה — נא לנסות שוב או לפנות לתמיכה."
  }
} as const;

export type ErrorCode = keyof typeof he.errors;

export function messageFor(code: string): string {
  return messageForFrom(he, code);
}

export function messageForFrom(strings: { errors: Record<string, string> }, code: string): string {
  return strings.errors[code] ?? strings.errors["VC-SYS-001"] ?? "An error occurred.";
}
